/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import DiscoveryCollectionsRatingsReviewsQuery from 'bundles/browse/components/DiscoveryCollectionsRatingsReviewsQuery.graphql';
import type { PremiumProductCollectionsQuery } from 'bundles/browse/components/Domain/queries/__generated__/getPremiumProductCollectionsByDomainQuery';
import getPremiumProductCollectionsByDomain from 'bundles/browse/components/Domain/queries/getPremiumProductCollectionsByDomainQuery.graphql';
import type { DiscoveryCollectionsRatingsReviewQuery as DiscoveryCollectionsRatingsReviewsQueryData } from 'bundles/browse/components/__generated__/DiscoveryCollectionsRatingsReviewsQuery';
import { ProductVariantNames } from 'bundles/browse/constants';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import NoSearchResultsMessage from 'bundles/search-common/components/NoSearchResultsMessage';
import SearchCards from 'bundles/search-common/components/search-cards/SearchCards';
import { NUM_NO_RESULTS_RECS_PER_PRODUCT_TYPE, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import {
  combineDegreeAndDiscoveryData,
  formatDiscoveryData,
} from 'bundles/search-v2/utils/withNoSearchResultsRecommendations';

import _t from 'i18n!nls/search';

type EventingData = {
  searchIndex: string;
  searchIndexPosition: number;
  page?: string | undefined;
  searchQuery?: string | undefined;
  filtersApplied?: Record<string, string[]> | undefined;
};

type Props = {
  eventingData: EventingData;
};
const SearchNoResultsRecommendations = ({ eventingData }: Props) => {
  const showNoResultsSearch = GrowthDiscoveryEpicClient.get('showUpdatedNoResultsPage');
  const { data: newDiscoveryCollectionsData, loading: newDiscoveryCollectionsLoading } =
    useQuery<DiscoveryCollectionsRatingsReviewsQueryData>(DiscoveryCollectionsRatingsReviewsQuery, {
      variables: {
        contextType: 'PAGE',
        contextId: 'new-search-zero-state',
      },
      context: { clientName: 'gatewayGql' },
      skip: !showNoResultsSearch,
    });
  const { data: discoveryCollectionsData, loading: discoveryCollectionsLoading } =
    useQuery<DiscoveryCollectionsRatingsReviewsQueryData>(DiscoveryCollectionsRatingsReviewsQuery, {
      variables: {
        contextType: 'PAGE',
        contextId: 'search-zero-state',
      },
      context: { clientName: 'gatewayGql' },
      skip: showNoResultsSearch,
    });
  const { data: premiumProductCollectionData, loading: premiumProductsCollectionLoading } =
    useQuery<PremiumProductCollectionsQuery>(getPremiumProductCollectionsByDomain, {
      variables: {
        domains: [],
        productVariants: [ProductVariantNames.BachelorsDegree, ProductVariantNames.MastersDegree],
      },
      context: {
        clientName: 'gatewayGql',
      },
      skip: showNoResultsSearch,
    });
  const discoveryCollections = discoveryCollectionsData?.DiscoveryCollections?.queryCollections;
  const premiumProductCollection = premiumProductCollectionData?.PremiumProductCollections?.queryCollection;
  const newDiscoveryCollections = newDiscoveryCollectionsData?.DiscoveryCollections?.queryCollections;
  const recommendationHits = showNoResultsSearch
    ? formatDiscoveryData(newDiscoveryCollections || [], newDiscoveryCollectionsLoading)
    : combineDegreeAndDiscoveryData({
        discoveryCollections,
        discoveryCollectionsLoading,
        premiumProductCollection,
        premiumProductsCollectionLoading,
      });
  const recommendationHitsLoading =
    discoveryCollectionsLoading || premiumProductsCollectionLoading || newDiscoveryCollectionsLoading;
  const { getSearchResults, isLoading } = useSearch();
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const { query } = searchData || {};
  const isConsumerSearchPage = useIsConsumerSerp();
  const noSearchResultsCollectionData = {
    collectionId: newDiscoveryCollections?.[0]?.id ?? discoveryCollections?.[0]?.id,
    collectionName: newDiscoveryCollections?.[0]?.label ?? discoveryCollections?.[0]?.label,
    collectionIndex: 0,
  };

  return (
    <div role="region" aria-label={_t('Search Results')} id="searchResults">
      <NoSearchResultsMessage query={query} recommendationHitsLength={recommendationHits?.length} />
      <SearchCards
        hits={recommendationHits}
        resultsPerPage={NUM_NO_RESULTS_RECS_PER_PRODUCT_TYPE * 2}
        noResults={true}
        searchCardImageProps={{ fit: 'crop' }}
        eventingData={eventingData}
        isLoading={recommendationHitsLoading || isLoading}
        isSearchPage={isConsumerSearchPage}
        noSearchResultsCollectionData={noSearchResultsCollectionData}
      />
    </div>
  );
};

export default SearchNoResultsRecommendations;
